// @flow
import React, { type Node, Fragment } from 'react';
import ActivityAvatar from 'app/common/react/components/Activities/ActivityItems/ActivityAvatar';
import CommentAttribution from 'app/common/react/components/Comments/CommentAttribution';
import Reactions from 'app/common/react/components/SocialReactions/Reactions';
import TaggedText from 'app/common/react/components/Activities/ActivityItems/TaggedText';
import Testable from 'app/common/react/ui/Testable';
import timer from 'app/common/lib/timer';
import TruncatedText from 'app/common/react/components/Activities/ActivityItems/TruncatedText';
import { Box } from 'gestalt';
import { type ActivityUser } from 'app/common/redux/reducers/activityItems';
import { type CloseupViewType } from 'app/common/react/components/closeup/types';
import { withFeaturedComment } from 'app/common/react/components/Comments/FeaturedCommentContext';
import ActivityItemCreate, {
  type ActivityItemEditData,
} from 'app/common/react/components/Activities/ActivityItemCreate/ActivityItemCreate';

const FEATURED_HIGHLIGHT_TRANSITION_START_MS = 1500;
const FEATURED_HIGHLIGHT_TRANSITION_DURATION_MS = 1000;

type Props = {|
  allowLink?: boolean,
  avatarSize: 'sm' | 'md',
  commentCount: number,
  contentImage?: Object,
  contentImageSignature?: string,
  contentText: string,
  cropImages?: boolean,
  flyout: Node,
  handleOnEditCancel: () => void,
  handleOnEdited: (ActivityItemEditData) => void,
  handleLikeClick: ({ event: SyntheticMouseEvent<> }) => void,
  handleLikeCountClick: ({
    event: SyntheticMouseEvent<HTMLDivElement> | SyntheticKeyboardEvent<HTMLDivElement>,
  }) => void,
  handlePinClick?: () => void,
  handleReplyClick: () => void,
  handleReplyCountClick?: () => void,
  hideAddObjects?: boolean,
  hideReplyCount?: boolean,
  inEditMode: boolean,
  featuredCommentId?: string,
  isLikedByMe: boolean,
  parentObject: {
    id: string,
  },
  pins?: Array<Object>,
  reactionCounts: Object,
  taggedUsers: Array<Object>,
  tags: Array<Object>,
  truncateText?: boolean,
  commentId: string,
  createdAt: string,
  commentAuthor: ActivityUser,
  // TODO: define these
  type: string,
  variant: CloseupViewType,
|};

const BORDER_STYLE = {
  border: '1px solid #efefef',
  borderRadius: '16px',
};

// used to prevent elements from jumping slightly when we hide the border in edit mode
const FAKE_BORDER_STYLE = {
  border: '1px solid transparent',
  borderRadius: '16px',
};

type State = {|
  isFeaturedHighlighting: boolean,
|};

export class Comment extends React.PureComponent<Props, State> {
  static defaultProps = { allowLink: false, avatarSize: 'sm' };
  state = { isFeaturedHighlighting: this.props.commentId === this.props.featuredCommentId };

  componentDidMount() {
    if (this.props.commentId === this.props.featuredCommentId) {
      timer.setTimeout(() => {
        this.setState({ isFeaturedHighlighting: false });
      }, FEATURED_HIGHLIGHT_TRANSITION_START_MS);
    }
  }

  renderEditMode = () => {
    const {
      contentImage,
      contentImageSignature,
      contentText,
      handleOnEdited,
      handleOnEditCancel,
      hideAddObjects,
      parentObject,
      pins,
      tags,
      taggedUsers,
      type,
    } = this.props;
    return (
      <ActivityItemCreate
        addedObjects={pins}
        addedImage={contentImage}
        hideAddObjects={hideAddObjects}
        imageSignature={contentImageSignature}
        objectType={type}
        onAddItem={() => {}}
        onCancelItem={handleOnEditCancel}
        onEditItem={handleOnEdited}
        onEnter={() => {}}
        parentObjectId={parentObject.id}
        statusType="textInFlightEdit"
        tags={tags}
        taggedUsers={taggedUsers}
        text={contentText}
        widthAuto
      />
    );
  };

  render() {
    const {
      allowLink,
      avatarSize,
      createdAt,
      commentCount,
      commentId,
      commentAuthor,
      contentText: text,
      flyout,
      handleLikeClick,
      handleReplyClick,
      handleReplyCountClick,
      hideReplyCount,
      inEditMode,
      isLikedByMe,
      reactionCounts,
      tags,
      taggedUsers,
      handleLikeCountClick,
      truncateText,
      variant,
    } = this.props;

    const { isFeaturedHighlighting } = this.state;
    const borderStyle = !inEditMode ? BORDER_STYLE : FAKE_BORDER_STYLE;

    return (
      <Testable id="canonical-comment">
        <Box display="flex">
          <Box marginRight={2}>
            <ActivityAvatar user={commentAuthor} size={avatarSize} />
          </Box>
          <Box flex="grow">
            <Box
              dangerouslySetInlineStyle={{
                __style: {
                  ...borderStyle,
                  transition: `${FEATURED_HIGHLIGHT_TRANSITION_DURATION_MS}ms`,
                  backgroundColor: isFeaturedHighlighting ? 'lightGray' : null,
                  wordBreak: 'break-all',
                },
              }}
              padding={inEditMode ? 0 : 3}
            >
              {inEditMode ? (
                this.renderEditMode()
              ) : (
                // content
                <Fragment>
                  <CommentAttribution
                    commentId={commentId}
                    createdAt={createdAt}
                    user={commentAuthor}
                    variant={variant}
                  />
                  {truncateText ? (
                    <TruncatedText
                      allowLink={allowLink}
                      allowReveal
                      size="sm"
                      taggedUsers={taggedUsers}
                      tags={tags}
                      text={text}
                    />
                  ) : (
                    <TaggedText
                      allowLink={allowLink}
                      size="sm"
                      tags={tags}
                      taggedUsers={taggedUsers}
                      text={text}
                    />
                  )}
                </Fragment>
              )}
            </Box>
            {!inEditMode && (
              <Box padding={1}>
                <Reactions
                  commentCount={commentCount}
                  flyout={!inEditMode ? flyout : null}
                  hideReplyCount={hideReplyCount}
                  isLikedByMe={isLikedByMe}
                  onLikeClick={handleLikeClick}
                  onLikeCountClick={handleLikeCountClick}
                  onReplyClick={handleReplyClick}
                  onReplyCountClick={handleReplyCountClick}
                  reactionCounts={reactionCounts}
                  small
                  variant={variant}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Testable>
    );
  }
}

export default withFeaturedComment(Comment);
